import { useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { QR_LIST_REVIEW } from "../../../userReview/graphQl/ReviewAPI";

const DEFAULT_PAGINATION = {
  currentPage: 0,
  sizePage: 1000,
};

const areSetsEqual = (left, right) => {
  if (left === right) {
    return true;
  }

  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
};

const isReviewOwnedByUser = (opinion, userId) => {
  const authorId = opinion?.ownerId ?? opinion?.clientId ?? "";
  return String(authorId) === String(userId ?? "");
};

const isLegacyAnonymousReview = (opinions) => {
  return (
    Array.isArray(opinions) &&
    opinions.length > 0 &&
    opinions.every((opinion) => !opinion?.ownerId && !opinion?.clientId)
  );
};

export const useReviewedPublicationIds = ({
  publicationIds = [],
  userId,
  enabled = true,
}) => {
  const client = useApolloClient();
  const clientRef = useRef(client);
  const [reviewedPublicationIds, setReviewedPublicationIds] = useState(new Set());
  const [reviewedMarks, setReviewedMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  clientRef.current = client;

  const normalizedIds = useMemo(() => {
    return [...new Set((publicationIds ?? []).filter(Boolean).map(String))].sort();
  }, [publicationIds]);

  const normalizedIdsKey = useMemo(() => normalizedIds.join("|"), [normalizedIds]);
  const requestKey = `${enabled ? "1" : "0"}:${String(userId ?? "")}:${normalizedIdsKey}`;

  useEffect(() => {
    let cancelled = false;
    const isEnabled = enabled && Boolean(userId) && normalizedIds.length > 0;

    if (!isEnabled) {
      setReviewedPublicationIds((current) => (current.size === 0 ? current : new Set()));
      setLoading(false);
      setError(null);
      return undefined;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await Promise.allSettled(
          normalizedIds.map((tripPublicationId) =>
            clientRef.current.query({
              query: QR_LIST_REVIEW,
              variables: {
                tripPublicationId,
                paginationParams: DEFAULT_PAGINATION,
              },
              fetchPolicy: "network-only",
            })
          )
        );

        if (cancelled) return;

        const nextSet = new Set();
        const nextMarks = {};

        results.forEach((result, index) => {
          if (result.status !== "fulfilled") {
            return;
          }

          const opinions =
            result.value?.data?.QR_LIST_REVIEW?.data?.content?.opinions ?? [];
          const alreadyReviewed =
            opinions.some((opinion) => isReviewOwnedByUser(opinion, userId)) ||
            isLegacyAnonymousReview(opinions);

          if (alreadyReviewed) {
            nextSet.add(normalizedIds[index]);

            const own =
              opinions.find((opinion) => isReviewOwnedByUser(opinion, userId)) ?? opinions[0];
            const mark = Number(own?.globalMark ?? 0);
            if (mark > 0) {
              nextMarks[normalizedIds[index]] = mark;
            }
          }
        });

        setReviewedPublicationIds((current) =>
          areSetsEqual(current, nextSet) ? current : nextSet
        );
        setReviewedMarks(nextMarks);
      } catch (queryError) {
        if (!cancelled) {
          setError(queryError);
          setReviewedPublicationIds((current) => (current.size === 0 ? current : new Set()));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [requestKey]);

  return {
    reviewedPublicationIds,
    reviewedMarks,
    loading,
    error,
  };
};
